import type { ReactNode } from 'react';

import { makeStyles } from '@material-ui/core';
import { ToggleButtonGroup } from '@material-ui/lab';

import { RiBarChartHorizontalLine, RiListCheck } from '@remixicon/react';

import { TooltipToggleButton } from '../TooltipToggleButton';

export type ViewMode = 'Table' | 'Timeline' | string;

interface ViewModeOption {
  icon: ReactNode;
  name: ViewMode;
}

export const DEFAULT_VIEW_MODE_OPTIONS: ViewModeOption[] = [
  { icon: <RiListCheck size={20} />, name: 'Table' },
  { icon: <RiBarChartHorizontalLine size={20} />, name: 'Timeline' },
];

const useStyles = makeStyles({
  toggleButton: {
    height: 'var(--encore-spacing-looser-2)',
    width: 'var(--encore-spacing-looser-2)',
    borderColor: 'var(--decorative-subdued)',
  },
});

type ViewModeToggleProps = {
  mode: ViewMode;
  onModeChange: (newViewMode: ViewMode) => void;
  options?: ViewModeOption[];
  disabled?: boolean;
};

export const ViewModeToggle = ({
  mode,
  onModeChange,
  options = DEFAULT_VIEW_MODE_OPTIONS,
  disabled,
}: ViewModeToggleProps) => {
  const classes = useStyles();

  const changeMode = (newMode: ViewMode | null) =>
    onModeChange(newMode === null ? mode : newMode);

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={mode}
      onChange={(_e, value) => changeMode(value)}
      data-testid="toggle-view-mode"
    >
      {options.map(viewMode => (
        <TooltipToggleButton
          key={viewMode.name}
          className={classes.toggleButton}
          value={viewMode.name}
          disabled={disabled}
          aria-label={viewMode.name}
          TooltipProps={{ title: viewMode.name, enterDelay: 1000 }}
        >
          {viewMode.icon}
        </TooltipToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
